import React, { useEffect, useState, useCallback } from "react";
import * as LucideIcons from "lucide-react";
import { Plus, Trash2, ArrowUp, ArrowDown, Save } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const ICON_CHOICES = ["Heart", "Camera", "Film", "Plane", "Sparkles", "Award", "Star",
  "Shield", "Smile", "Gem", "Image", "Eye", "Clock", "Globe", "Sun", "Moon"];

function IconPicker({ value, onChange, testId }) {
  return (
    <div className="grid grid-cols-8 gap-2" data-testid={testId}>
      {ICON_CHOICES.map((name) => {
        const Icon = LucideIcons[name] || LucideIcons.Star;
        const active = value === name;
        return (
          <button key={name} type="button" onClick={() => onChange(name)}
            data-testid={`${testId}-${name}`}
            title={name}
            className={`p-3 border transition flex items-center justify-center ${active ? "border-[#D4AF37] bg-[#D4AF37]/15 text-[#D4AF37]" : "border-white/15 bg-black text-white/60 hover:border-white/40"}`}>
            <Icon size={18} />
          </button>
        );
      })}
    </div>
  );
}

function ListEditor({ endpoint, label, schema, sampleEmpty, testIdRoot, includeStepNumber = false }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.get(`/admin/${endpoint}`); setRows(r.data); }
    catch { toast.error(`Could not load ${label}`); }
  }, [endpoint, label]);

  useEffect(() => { load(); }, [load]);

  const patch = (i, k, v) => setRows(prev => prev.map((r, j) => j === i ? { ...r, [k]: v } : r));

  const save = async (row) => {
    setBusy(true);
    try {
      if (row.id) {
        await api.put(`/admin/${endpoint}/${row.id}`, row);
      } else {
        const r = await api.post(`/admin/${endpoint}`, row);
        row.id = r.data.id;
      }
      toast.success("Saved");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete "${row.title}"?`)) return;
    try { await api.delete(`/admin/${endpoint}/${row.id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Could not delete"); }
  };

  const move = async (i, dir) => {
    const j = i + dir; if (j < 0 || j >= rows.length) return;
    const reordered = [...rows]; [reordered[i], reordered[j]] = [reordered[j], reordered[i]];
    setRows(reordered);
    try { await api.post(`/admin/${endpoint}/reorder`, { ordered_ids: reordered.map(r => r.id).filter(Boolean) }); }
    catch { toast.error("Reorder failed"); load(); }
  };

  const add = () => setRows(prev => [...prev, { ...sampleEmpty, order: prev.length }]);

  return (
    <div className="space-y-3" data-testid={`${testIdRoot}-list`}>
      {rows.map((row, i) => {
        const Icon = LucideIcons[row.icon] || LucideIcons.Star;
        return (
          <div key={row.id || `new-${i}`} className="bg-[#0a0a0a] border border-white/10 p-5" data-testid={`${testIdRoot}-row-${i}`}>
            <div className="flex flex-wrap items-start gap-4">
              <div className="flex flex-col items-center gap-1 pt-2">
                <button onClick={() => move(i, -1)} disabled={i === 0} className="p-1 text-white/40 hover:text-[#D4AF37] disabled:opacity-30" title="Move up"><ArrowUp size={14} /></button>
                <Icon size={22} className="text-[#D4AF37]" />
                <button onClick={() => move(i, 1)} disabled={i === rows.length - 1} className="p-1 text-white/40 hover:text-[#D4AF37] disabled:opacity-30" title="Move down"><ArrowDown size={14} /></button>
              </div>
              <div className="flex-1 min-w-[260px] grid gap-3 md:grid-cols-2">
                {includeStepNumber && (
                  <label className="block">
                    <span className="text-[0.6rem] tracking-[0.25em] uppercase text-white/60">Step #</span>
                    <input type="number" value={row.step_number ?? (i + 1)} onChange={e => patch(i, "step_number", parseInt(e.target.value || "0"))}
                      className="mt-1 w-full bg-black border border-white/15 px-3 py-2 text-sm focus:border-[#D4AF37] outline-none" data-testid={`${testIdRoot}-row-${i}-step`} />
                  </label>
                )}
                <label className="block md:col-span-2">
                  <span className="text-[0.6rem] tracking-[0.25em] uppercase text-white/60">Title</span>
                  <input value={row.title || ""} onChange={e => patch(i, "title", e.target.value)}
                    className="mt-1 w-full bg-black border border-white/15 px-3 py-2 text-sm focus:border-[#D4AF37] outline-none" data-testid={`${testIdRoot}-row-${i}-title`} />
                </label>
                <label className="block md:col-span-2">
                  <span className="text-[0.6rem] tracking-[0.25em] uppercase text-white/60">Description</span>
                  <textarea rows={2} value={row.description || ""} onChange={e => patch(i, "description", e.target.value)}
                    className="mt-1 w-full bg-black border border-white/15 px-3 py-2 text-sm focus:border-[#D4AF37] outline-none" data-testid={`${testIdRoot}-row-${i}-desc`} />
                </label>
                <div className="md:col-span-2">
                  <span className="text-[0.6rem] tracking-[0.25em] uppercase text-white/60 mb-2 block">Icon</span>
                  <IconPicker value={row.icon || "Star"} onChange={(v) => patch(i, "icon", v)} testId={`${testIdRoot}-row-${i}-icon`} />
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <button onClick={() => save(row)} disabled={busy} className="btn-gold text-xs" data-testid={`${testIdRoot}-row-${i}-save`}>
                  <Save size={12} /> Save
                </button>
                {row.id && (
                  <button onClick={() => remove(row)} className="btn-ghost text-xs text-red-300 hover:text-red-500" data-testid={`${testIdRoot}-row-${i}-delete`}>
                    <Trash2 size={12} /> Delete
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })}
      <button onClick={add} className="btn-ghost w-full justify-center py-4 border-dashed" data-testid={`${testIdRoot}-add`}>
        <Plus size={14} /> Add {label.replace(/s$/, "")}
      </button>
    </div>
  );
}

export function WhyChooseAdmin() {
  return (
    <ListEditor
      endpoint="why-choose"
      label="Items"
      testIdRoot="why-choose"
      sampleEmpty={{ title: "", description: "", icon: "Star", order: 0 }}
    />
  );
}

export function ProcessStepsAdmin() {
  return (
    <ListEditor
      endpoint="process-steps"
      label="Steps"
      testIdRoot="process-steps"
      includeStepNumber
      sampleEmpty={{ step_number: 1, title: "", description: "", icon: "Sparkles", order: 0 }}
    />
  );
}
